// SUPIN runner microservice configuration.
//
// The runner is a dedicated Fly.io app (`telekinesis-supin-runner`) that
// compiles and executes the actual NASA SUPIN Fortran source code. The
// service implementation lives in `/supin-microservice/`.
//
// Override at build time with VITE_SUPIN_URL — useful for hitting a local
// instance (`docker run -p 8080:8080 supin-runner`) during development.
//
// STAGING PARITY (important): SUPIN is the only kernel the browser calls
// directly (no edge function), so it sends the user's Supabase JWT and the
// runner validates that JWT against its OWN {SUPABASE_URL}/auth/v1/user. The
// default runner above is wired to PROD Supabase, so a dev frontend pointed at
// STAGING (VITE_SUPABASE_URL = hybidvebuczznnfqaakv) gets a 401 — prod can't
// resolve a staging token. To develop against staging, set VITE_SUPIN_URL to a
// staging SUPIN runner whose SUPABASE_URL matches your frontend's project. See
// the runbook for standing one up: docs/deploy/supin-staging-runner.md.

export const SUPIN_URL: string =
  import.meta.env.VITE_SUPIN_URL ??
  'https://telekinesis-supin-runner.fly.dev/supin';

// Raw-namelist sibling of SUPIN_URL. POSTing { supin_in } here runs an
// already-rendered SUPIN.in verbatim (no param re-render) — this is what the
// "Edit in The Matrix" flow hits when the user hand-edits the namelist. Derived
// from SUPIN_URL so a VITE_SUPIN_URL override (e.g. a local instance) carries
// through automatically; override independently with VITE_SUPIN_RAW_URL.
export const SUPIN_RAW_URL: string =
  import.meta.env.VITE_SUPIN_RAW_URL ??
  SUPIN_URL.replace(/\/supin\/?$/, '/supin/raw');

// Client-side wall-clock budget for a single SUPIN run. The runner caps the
// Fortran process at SUPIN_TIMEOUT_SEC=120 (supin-microservice/Dockerfile) and
// gzips the STL on the way out; the 2D cases (K2/K4) build the heaviest mesh
// and ship the largest payloads (~10 MB gzipped, ~28 MB raw). 180s leaves
// headroom for a cold second Fly machine plus transfer. Without this bound the
// browser fetch waits forever, so a slow/stalled K2 run is indistinguishable
// from "never solves" — the user just stares at a spinner. See issue #26.
export const SUPIN_TIMEOUT_MS = 180_000;

export const INLET_LABELS: Record<string, string> = {
  K1: 'Axisymmetric Pitot',
  // K2 disabled — STL freezes the viewer (issue #26). Preserved for later.
  // K2: '2D External Compression',
  K3: 'Axisymmetric Spike',
  K4: '2D Bifurcated',
  // K5 disabled — solve exceeds the runner timeout (issue #26). Preserved for
  // later re-enable.
  // K5: 'Streamline-Traced',
};

// Response shape from POST /supin. Matches supin-microservice/main.py
// SupinResponse — keep in sync.
export type SupinRunnerResponse = {
  stl_b64?: string;
  supin_in?: string;
  summary?: string;
  table?: string;
  stdout_tail?: string;
  metrics?: Record<string, number | null>;
  error?: string;
};
